import { filter, pipe } from 'remeda';
import { scenarioMaps } from '../../../../../data/maps';
import { type CreateConfigurationRequest } from '@types';
import { EXPOSURE_MULTI_ITEM_GROUP_SIZE } from '../../../../../utils/exposureMultiItem';

import { type ScenarioConfigFormValues } from '../../forms/ScenarioForm/formSchema';

const chunk =
  (size: number) =>
  (arr: number[]): number[][] =>
    Array.from({ length: Math.ceil(arr.length / size) }, (_, i) =>
      arr.slice(i * size, i * size + size)
    );

const nonEmptyIds = (g: number[]): number[] =>
  g.filter(s => s != null && Number.isFinite(s) && s !== 0);

const hasAtLeastOneFood = (g: number[]): boolean => nonEmptyIds(g).length > 0;

const buildPerformanceConfig = (
  performance: ScenarioConfigFormValues['performance']
) => {
  const config: Record<string, unknown> = {};

  if (performance?.currentState) {
    config.currentState = performance.currentState;
  }

  if (typeof performance?.variation === 'number') {
    config.variation = performance.variation;
  }

  if (typeof performance?.duration === 'number') {
    config.duration = performance.duration;
  }

  return JSON.stringify(config);
};

const buildExposureConfig = (
  exposure: ScenarioConfigFormValues['exposure']
) => {
  const config: Record<string, unknown> = {};

  if (exposure && Array.isArray(exposure.itemIds)) {
    if (exposure.multiItem) {
      config.itemIds = pipe(
        exposure.itemIds,
        chunk(EXPOSURE_MULTI_ITEM_GROUP_SIZE),
        filter(hasAtLeastOneFood),
        (groups: number[][]) => groups.map(nonEmptyIds)
      );
    } else {
      config.itemIds = exposure.itemIds.filter(
        id => Number.isFinite(id) && id !== 0
      );
    }
  }

  if (typeof exposure?.multiItem === 'boolean') {
    config.multiItem = exposure.multiItem;
  }

  if (typeof exposure?.hasTutorial === 'boolean') {
    config.hasTutorial = exposure.hasTutorial;
  }

  if (typeof exposure?.userSpawnPosition === 'string') {
    config.userSpawnPosition = exposure.userSpawnPosition;
  }

  return JSON.stringify(config);
};

const buildBodyImageConfig = () =>
  JSON.stringify({
    mode: 'body-image',
  });

export function buildScenarioConfigurationPayload({
  formValues,
  patientId,
  deviceId,
  sensorId,
}: {
  formValues: ScenarioConfigFormValues;
  patientId: string;
  deviceId: string;
  sensorId?: string;
}): CreateConfigurationRequest {
  if (formValues.scenarioType === 'performance') {
    const map =
      formValues.performance.map || scenarioMaps.performance?.[0]?.value || '';

    const payload: CreateConfigurationRequest = {
      config: buildPerformanceConfig(formValues.performance),
      deviceId: Number(deviceId),
      patientId: Number(patientId),
      scenario: map,
      map: map,
      type: formValues.scenarioType,
    };

    if (sensorId) {
      payload.sensor_id = Number(sensorId);
    }

    if (Number.isNaN(payload.deviceId) || Number.isNaN(payload.patientId)) {
      throw new Error('Identificativi utente o dispositivo non validi');
    }

    return payload;
  }

  if (formValues.scenarioType === 'exposure') {
    const map =
      formValues.exposure?.map ?? scenarioMaps.exposure?.[0]?.value ?? '-';
    const payload: CreateConfigurationRequest = {
      config: buildExposureConfig(formValues.exposure),
      deviceId: Number(deviceId),
      patientId: Number(patientId),
      scenario: map,
      map: map,
      type: formValues.scenarioType,
    };

    if (sensorId) {
      payload.sensor_id = Number(sensorId);
    }

    if (Number.isNaN(payload.deviceId) || Number.isNaN(payload.patientId)) {
      throw new Error('Identificativi utente o dispositivo non validi');
    }

    return payload;
  }

  if (formValues.scenarioType === 'body_image') {
    const map = 'body-image';
    const payload: CreateConfigurationRequest = {
      config: buildBodyImageConfig(),
      deviceId: Number(deviceId),
      patientId: Number(patientId),
      scenario: map,
      map,
      type: formValues.scenarioType,
    };

    if (sensorId) {
      payload.sensor_id = Number(sensorId);
    }

    if (Number.isNaN(payload.deviceId) || Number.isNaN(payload.patientId)) {
      throw new Error('Identificativi utente o dispositivo non validi');
    }

    return payload;
  }

  throw new Error('Scenario type non supportato');
}
